/**
 * The wheel tools, in one list.
 *
 * Single source for the header's Tools dropdown, the mobile drawer, the
 * homepage strip, and the gallery's related-tools row — so a new landing page
 * becomes reachable from every one of them by adding a single entry here
 * instead of editing four components and forgetting the fourth.
 *
 * `short` is for the dropdown, where horizontal space is tight; `label` is the
 * full name used wherever there is room for it.
 */
export interface ToolLink {
  to: string;
  /** Full name, e.g. for cards and the mobile drawer. */
  label: string;
  /** Compact name for the desktop dropdown. */
  short: string;
  /** Decorative — always paired with the label, never the only signal. */
  emoji: string;
  blurb: string;
}

export const TOOLS: ToolLink[] = [
  { to: '/image-picker-wheel/',      label: 'Image Picker Wheel',     short: 'Image Wheel',      emoji: '🖼️', blurb: 'Upload pictures and spin a visual wheel to pick one at random.' },
  { to: '/weighted-wheel-spinner/',  label: 'Weighted Wheel Spinner', short: 'Weighted Wheel',   emoji: '⚖️', blurb: 'Set custom odds for every option and see each probability before you spin.' },
  { to: '/yes-no-wheel/',           label: 'Yes or No Wheel',        short: 'Yes or No',       emoji: '🎯', blurb: 'An instant random yes or no when the answer could genuinely go either way.' }, // FIX-1
  { to: '/classroom-name-picker/',  label: 'Classroom Name Picker',  short: 'Classroom',       emoji: '🏫', blurb: 'Call on students at random instead of the same three raised hands.' }, // FIX-1
  { to: '/giveaway-wheel/',         label: 'Giveaway Wheel',         short: 'Giveaway',        emoji: '🎁', blurb: 'Draw prize winners live, with auto-remove for multi-prize giveaways.' }, // FIX-1
  { to: '/decision-wheel/',         label: 'Decision Wheel',         short: 'Decision Wheel',  emoji: '🤔', blurb: 'Add your options and let the wheel break the deadlock for you.' }, // FIX-1
  { to: '/random-team-generator/',  label: 'Random Team Generator',  short: 'Team Generator',  emoji: '👥', blurb: 'Split any group into balanced random teams, one spin at a time.' }, // FIX-1
  { to: '/number-wheel/',           label: 'Number Wheel',           short: 'Number Wheel',    emoji: '🔢', blurb: 'Pick a random number from any range, with 1–6 through 1–100 presets.' }, // FIX-1
  { to: '/prize-wheel/',            label: 'Prize Wheel',            short: 'Prize Wheel',     emoji: '🏆', blurb: 'A spin-to-win wheel for events, stands, promotions, and streams.' }, // FIX-1
  { to: '/wheel-of-fortune-maker/', label: 'Wheel of Fortune Maker', short: 'Fortune Maker',   emoji: '🎡', blurb: 'Build a custom wheel out of any labels and share or embed it.' }, // FIX-1
  { to: '/trivia-wheel/',           label: 'Trivia Category Wheel',  short: 'Trivia Wheel',    emoji: '🧠', blurb: 'Spin for the next quiz topic so nobody can prepare for the round.' }, // FIX-1
  { to: '/spin-the-bottle/',        label: 'Spin the Bottle',        short: 'Spin the Bottle', emoji: '🍾', blurb: 'The party classic, without a bottle — picks a random player fairly.' }, // FIX-1 + FIX-2
  { to: '/random-letter-wheel/',    label: 'Random Letter Wheel',    short: 'Letter Wheel',    emoji: '🔤', blurb: 'Spin the alphabet for word games, vocabulary drills, and name prompts.' }, // FIX-1 + FIX-2
  // Gaming & Pop Culture cluster
  { to: '/anime-character-picker-wheel/',       label: 'Anime Character Picker',       short: 'Anime Picker',       emoji: '⚔️', blurb: 'Spin for a random anime character — for challenges, cosplay, or trivia.' },
  { to: '/fortnite-drop-spot-generator-wheel/', label: 'Fortnite Drop Spot Generator', short: 'Fortnite Drop Spot', emoji: '🪂', blurb: 'Spin for a random Fortnite landing spot before the bus crosses the map.' },
  { to: '/valorant-agent-picker-wheel/',        label: 'Valorant Agent Picker',        short: 'Valorant Agent',     emoji: '🎮', blurb: 'Spin for a random Valorant agent — for challenge runs and custom games.' },
  { to: '/roblox-game-selector-wheel/',         label: 'Roblox Game Selector',         short: 'Roblox Game',        emoji: '🟥', blurb: 'Spin to pick a random Roblox game — end the what-to-play debate.' },
];

/**
 * Features that act on wheels rather than being a wheel.
 *
 * Kept separate from TOOLS because they are a different kind of thing: TOOLS
 * answers "what do you want to spin?", these answer "what do you want to do
 * with the wheels you have?". Grouping them together in one flat list is what
 * made both read as yet another wheel type and get skipped.
 *
 * Both were reachable only from the footer before this, which is where features
 * go to be missed.
 */
export const WORKSPACE_LINKS: ToolLink[] = [
  { to: '/linked-wheels/',       label: 'Linked Wheels',     short: 'Linked Wheels',     emoji: '🔗', blurb: 'Chain spinners together so one result feeds the next.' }, // FIX-1
  { to: '/embed-spinner-wheel/', label: 'Embed Spinner Wheel', short: 'Embed Wheel',      emoji: '🧩', blurb: 'Add the complete interactive wheel to another website with an iframe.' },
  // 📋 rather than the 🏫 originally specified: Classroom Name Picker already
  // uses 🏫 in the list directly above this one, and two identical glyphs in a
  // single dropdown read as a mistake.
  { to: '/classes/',             label: 'Your Classes',      short: 'Your Classes',      emoji: '📋', blurb: 'Save your class rosters and reuse them instantly.' }, // FIX-1
  { to: '/classroom/resources/', label: 'Teacher Resources', short: 'Teacher Resources', emoji: '📚', blurb: 'Guides on random selection in the classroom.' }, // FIX-1
  { to: '/names/',               label: 'Name Lists',        short: 'Name Lists',        emoji: '✨', blurb: 'Curated lists for babies, dogs, characters and usernames.' }, // FIX-1
];

/**
 * The three pages promoted on the homepage and in the how-to callout.
 *
 * A subset of WORKSPACE_LINKS rather than a second list, so a description
 * cannot say one thing in the nav and another on the homepage. Name Lists is
 * deliberately absent: it is browsing content rather than a capability, and a
 * four-card row loses the emphasis a three-card one has.
 */
export const FEATURED_LINKS: ToolLink[] = WORKSPACE_LINKS.filter(
  l => l.to !== '/names/' && l.to !== '/embed-spinner-wheel/', // FIX-1: keep the original three-card row
);

/**
 * The "For Educators" menu.
 *
 * A separate nav item rather than a group inside Tools. Teachers are the
 * clearest single audience this site has, and the things they need are spread
 * across three different kinds of page — two wheels, a saved-roster view, and
 * written guides. Tools answers "what do you want to spin?", which is the wrong
 * question for someone who arrived from a search about calling on students.
 *
 * The first four already existed and are simply gathered here; nothing is moved
 * out of Tools, which keeps its own grouping intact.
 */
export const EDUCATOR_LINKS: ToolLink[] = [
  { to: '/classroom-name-picker/',  label: 'Classroom Name Picker',  short: 'Classroom Picker',  emoji: '🏫', blurb: 'Call on students at random, visibly fairly.' }, // FIX-1
  { to: '/random-team-generator/',  label: 'Random Team Generator',  short: 'Team Generator',    emoji: '👥', blurb: 'Split a class into balanced teams, one spin at a time.' }, // FIX-1
  { to: '/classes/',                label: 'Your Classes',           short: 'Your Classes',      emoji: '📋', blurb: 'Save your class rosters and reuse them instantly.' }, // FIX-1
  { to: '/classroom/resources/',    label: 'Teacher Resources',      short: 'Teacher Resources', emoji: '📚', blurb: 'Guides on random selection in the classroom.' }, // FIX-1
];

/** Sits below a divider in the menu — writing rather than a tool. */
export const EDUCATOR_BLOG_LINK: ToolLink = {
  to: '/blog/?category=educators', // FIX-1: trailing slash before query string
  label: 'Blog for Educators',
  short: 'Blog',
  emoji: '📖',
  blurb: 'Classroom activities and ideas.',
};

/**
 * Homepage use-case cards.
 *
 * Broader than EDUCATOR_LINKS on purpose: the section is headed "Built for
 * Classrooms & Events", so it has to speak to the event half of that too.
 */
export const USE_CASE_CARDS: ToolLink[] = [
  { to: '/classroom-name-picker/', label: 'Classrooms',        short: 'Classrooms',    emoji: '🏫', blurb: 'Call on students randomly, build teams, assign seats. No bias, no arguments.' }, // FIX-1
  { to: '/giveaway-wheel/',        label: 'Events & Giveaways', short: 'Events',       emoji: '🎁', blurb: 'Run live prize draws with auto-remove. Transparent and crowd-ready.' }, // FIX-1
  { to: '/random-team-generator/', label: 'Team Building',      short: 'Team Building', emoji: '👥', blurb: 'Split any group into balanced random teams in seconds.' }, // FIX-1
];

/**
 * Mega-panel: left column — educator-focused tools.
 *
 * Matches the spec's exact listing. Pulled from existing ToolLink entries where
 * possible so descriptions stay consistent with the dropdown and homepage.
 */
export const MEGA_PANEL_EDUCATOR: ToolLink[] = [
  { to: '/classroom-name-picker/',  label: 'Classroom Name Picker',  short: 'Classroom Picker',  emoji: '🏫', blurb: 'Call on students at random, visibly fairly.' }, // FIX-1
  { to: '/giveaway-wheel/',         label: 'Giveaway Wheel',         short: 'Giveaway',          emoji: '🎁', blurb: 'Draw prize winners live, with auto-remove for multi-prize giveaways.' }, // FIX-1
  { to: '/random-team-generator/',  label: 'Random Team Generator',  short: 'Team Generator',    emoji: '👥', blurb: 'Split a class into balanced teams, one spin at a time.' }, // FIX-1
  { to: '/classroom/resources/',    label: 'Teacher Resources',      short: 'Teacher Resources', emoji: '📚', blurb: 'Guides on random selection in the classroom.' }, // FIX-1
  { to: '/classes/',                label: 'Your Classes',           short: 'Your Classes',      emoji: '📋', blurb: 'Save your class rosters and reuse them instantly.' }, // FIX-1
];

/**
 * Mega-panel: right column — advanced / general-purpose tools.
 */
export const MEGA_PANEL_TOOLS: ToolLink[] = [
  { to: '/linked-wheels/',          label: 'Linked Wheels',          short: 'Linked Wheels',     emoji: '🔗', blurb: 'Chain spinners together so one result feeds the next.' }, // FIX-1
  { to: '/decision-wheel/',         label: 'Decision Wheel',         short: 'Decision Wheel',    emoji: '🤔', blurb: 'Add your options and let the wheel break the deadlock for you.' }, // FIX-1
  { to: '/prize-wheel/',            label: 'Prize Wheel',            short: 'Prize Wheel',       emoji: '🏆', blurb: 'A spin-to-win wheel for events, stands, promotions, and streams.' }, // FIX-1
  { to: '/wheel-of-fortune-maker/', label: 'Wheel of Fortune Maker', short: 'Fortune Maker',     emoji: '🎡', blurb: 'Build a custom wheel out of any labels and share or embed it.' }, // FIX-1
  { to: '/number-wheel/',           label: 'Number Wheel',           short: 'Number Wheel',      emoji: '🔢', blurb: 'Pick a random number from any range, with 1–6 through 1–100 presets.' }, // FIX-1
  { to: '/yes-no-wheel/',           label: 'Yes or No Wheel',        short: 'Yes or No',         emoji: '🎯', blurb: 'An instant random yes or no when the answer could genuinely go either way.' }, // FIX-1
];

/**
 * Mega-panel: third column — Gaming & Pop Culture wheels.
 */
export const MEGA_PANEL_GAMING: ToolLink[] = [
  { to: '/anime-character-picker-wheel/',       label: 'Anime Character Picker',       short: 'Anime Picker',       emoji: '🌸', blurb: 'Spin for a random anime character — for challenges, cosplay, or trivia.' },
  { to: '/fortnite-drop-spot-generator-wheel/', label: 'Fortnite Drop Spot Generator', short: 'Fortnite Drop Spot', emoji: '🎮', blurb: 'Spin for a random Fortnite landing spot before the bus crosses the map.' },
  { to: '/valorant-agent-picker-wheel/',        label: 'Valorant Agent Picker',        short: 'Valorant Agent',     emoji: '🔫', blurb: 'Spin for a random Valorant agent — for challenge runs and custom games.' },
  { to: '/roblox-game-selector-wheel/',         label: 'Roblox Game Selector',         short: 'Roblox Game',        emoji: '👾', blurb: 'Spin to pick a random Roblox game — end the what-to-play debate.' },
];

export const TOOLS_BY_PATH: Record<string, ToolLink> = Object.fromEntries(
  TOOLS.map(t => [t.to, t]), // FIX-1: keys now include trailing slash, matching canonical URLs
);

/** Every tool except the one you are already on. */
export function otherTools(currentPath: string, limit?: number): ToolLink[] {
  // FIX-1: normalise currentPath so callers passing '/foo' still match '/foo/'
  const normalised = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
  const rest = TOOLS.filter(t => t.to !== normalised);
  return limit ? rest.slice(0, limit) : rest;
}
